import React, {
  PropsWithChildren,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { CancelSubscriptionContext } from "@shared/features/settings/subscription/CancelSubscription.context.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { displayResultMessage } from "utils/misc.tsx";
import { CancellationReason, LanguageNameSpaces } from "@shared/types";
import { MAX_REASON_LENGTH } from "@shared/features/settings/constants.ts";
import { useGetSubscriptionPlans } from "@shared/features/settings/subscription/hooks/misc.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import { CancellationStep } from "@shared/features/settings/subscription/types.ts";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { BILLING_SETTINGS_PATH } from "common/constants.ts";
import { useHistory } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import NewLookBackLink from "features/settings/NewLook/NewLookBackLink.tsx";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { toFixed } from "@shared/utils/numbers.ts";

const CancelSubscriptionProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const [step, setStep] = useState(CancellationStep.MISSING_FEATURES);
  const [reason, setReason] = useState<CancellationReason | undefined>(
    undefined
  );
  const [otherReasonDescription, setOtherReasonDescription] =
    useState<string>("");
  const history = useHistory();
  const { isNewLook } = useTheme();

  const { plans, isLoading: isLoadingPlans } = useGetSubscriptionPlans();
  const plan = user ? plans?.[user.subscription_plan_type] : undefined;

  const [cancelSubscription, { isLoading: isLoadingCancelSubscription }] =
    sharedLib.sharedApi.settings.useCancelSubscriptionMutation();
  const [saveCancellationReason] =
    sharedLib.sharedApi.settings.useSaveCancellationReasonMutation();
  const skipLoseDiscount = user?.isNativeSubscription();
  const { data: currentDiscount, isLoading: isLoadingCurrentDiscount } =
    sharedLib.sharedApi.settings.useGetCurrentDiscountQuery(
      step > CancellationStep.MISSING_FEATURES && !skipLoseDiscount
        ? undefined
        : skipToken
    );

  const userDuration = user?.package_duration ?? 30;
  const originalPlanPrice = plan?.[userDuration]?.price || 0;
  const originalBasePlanPrice = (plan?.[30]?.price || 0) * (userDuration / 30);
  const currentDiscountAmount = originalBasePlanPrice * (currentDiscount || 0);
  const currentPrice = toFixed(
    originalBasePlanPrice - currentDiscountAmount,
    2
  );

  const baseIsInvalidLoseDiscount =
    !isLoadingCurrentDiscount &&
    ((currentDiscount || 0) <= 0 ||
      currentPrice >= originalPlanPrice ||
      skipLoseDiscount);
  const isInvalidLoseDiscount =
    step === CancellationStep.LOSE_DISCOUNT && baseIsInvalidLoseDiscount;
  const isLoading =
    isLoadingCancelSubscription ||
    isLoadingCurrentDiscount ||
    isLoadingPlans ||
    isInvalidLoseDiscount;

  useLayoutEffect(() => {
    if (isInvalidLoseDiscount) {
      setStep(CancellationStep.REASON);
    }
  }, [isInvalidLoseDiscount]);

  useLayoutEffect(() => {
    if (reason !== CancellationReason.OTHER && otherReasonDescription) {
      setOtherReasonDescription("");
    }
  }, [reason]);

  const cancelAutoRenewal = () => {
    if (!reason) return;
    saveCancellationReason({
      reason_id: reason,
      additional_text: otherReasonDescription,
    }).catch(() => {});
    displayResultMessage(
      cancelSubscription().unwrap(),
      t("cancel_subscription.cancel_auto_renewal_success"),
      t("cancel_subscription.cancel_auto_renewal_failed")
    )
      .then(() => {
        setStep(CancellationStep.SUCCESS);
      })
      .catch(() => {});
  };

  const onNextClick = () => {
    if (step === CancellationStep.ARE_YOU_SURE) {
      cancelAutoRenewal();
    } else if (step === CancellationStep.MISSING_FEATURES && skipLoseDiscount) {
      setStep(step + 2);
    } else {
      setStep(step + 1);
    }
  };

  const onBackButtonClick = () => {
    const isInvalidPreviousStep =
      step === CancellationStep.REASON && baseIsInvalidLoseDiscount;
    if (!step) {
      history.push(BILLING_SETTINGS_PATH);
    } else if (isInvalidPreviousStep) {
      setStep(step - 2);
    } else {
      setStep(step - 1);
    }
  };

  const isInvalidReasonDescription =
    reason === CancellationReason.OTHER &&
    otherReasonDescription.length > MAX_REASON_LENGTH;

  const getBackButton = () => {
    if (step === CancellationStep.SUCCESS) return;
    const label =
      step === CancellationStep.MISSING_FEATURES
        ? t("cancel_subscription.back_to_billing")
        : t("cancel_subscription.back_button");
    if (isNewLook) {
      return (
        <NewLookBackLink onClick={onBackButtonClick}>{label}</NewLookBackLink>
      );
    }
    return (
      <BackWrapper onClick={onBackButtonClick}>
        <ArrowLeftOutlined style={{ marginRight: 8, marginBottom: 2 }} />
        {step === CancellationStep.MISSING_FEATURES
          ? t("cancel_subscription.back_to_billing")
          : t("cancel_subscription.back_button")}
      </BackWrapper>
    );
  };

  const value = useMemo(
    () => ({
      step,
      setStep,
      onNextClick,
      isLoadingCancelSubscription,
      reason,
      setReason,
      otherReasonDescription,
      setOtherReasonDescription,
      isInvalidReasonDescription,
      plans,
      currentDiscount,
      plan,
      isLoading,
      onBackButtonClick,
      getBackButton,
    }),
    [
      step,
      isLoadingCancelSubscription,
      reason,
      otherReasonDescription,
      isInvalidReasonDescription,
      plans,
      currentDiscount,
      plan,
      isLoading,
    ]
  );

  return (
    <CancelSubscriptionContext.Provider value={value}>
      {children}
    </CancelSubscriptionContext.Provider>
  );
};

export default CancelSubscriptionProvider;

const BackWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  margin-bottom: 16px;
  cursor: pointer;
`;
